// src/utils/fullscreen.ts
/**
 * Fullscreen-Helper mit Präferenz-Speicherung
 *
 * Strategie:
 * - Beim ersten beliebigen User-Tap in Fullscreen wechseln
 * - Präferenz in localStorage merken
 * - Bei jedem App-Start automatisch aktivieren (wartet auf ersten Tap)
 * - User kann jederzeit via ESC/Geste raus (Präferenz bleibt)
 */

const FS_PREF_KEY = "coupleup-fullscreen-enabled";

/**
 * Robuste Fullscreen-Aktivierung mit Fallbacks für alle Browser
 * @param el - Element für Fullscreen (default: documentElement)
 */
function requestFullscreenWithFallbacks(el: HTMLElement): Promise<void> {
  const anyEl = el as unknown as {
    webkitRequestFullscreen?: () => Promise<void>;
    msRequestFullscreen?: () => Promise<void>;
  };
  return (
    el.requestFullscreen?.() ||
    anyEl.webkitRequestFullscreen?.() ||
    anyEl.msRequestFullscreen?.() ||
    Promise.reject(new Error("Fullscreen not supported"))
  );
}

/**
 * Versucht Fullscreen zu aktivieren (nur wenn noch nicht aktiv)
 * @param el - Element für Fullscreen (default: documentElement)
 */
export async function tryEnterFullscreen(
  el: HTMLElement = document.documentElement
): Promise<boolean> {
  // Bereits im Fullscreen? Nichts tun
  if (document.fullscreenElement) return true;

  try {
    await requestFullscreenWithFallbacks(el);
    // Erfolg → Präferenz speichern
    localStorage.setItem(FS_PREF_KEY, "1");
    return true;
  } catch {
    // Fehler (z.B. keine User-Gesture, iOS-Block)
    return false;
  }
}

/**
 * Verlässt Fullscreen
 */
export async function exitFullscreen(): Promise<void> {
  if (document.fullscreenElement) {
    try {
      await document.exitFullscreen();
    } catch {
      // Ignore exit errors
    }
  }
}

/**
 * Prüft, ob User Fullscreen bereits aktiviert hat (Präferenz)
 */
export function hasFullscreenPreference(): boolean {
  return localStorage.getItem(FS_PREF_KEY) === "1";
}

/**
 * Löscht Fullscreen-Präferenz (z.B. für Settings-Toggle)
 */
export function clearFullscreenPreference(): void {
  localStorage.removeItem(FS_PREF_KEY);
}

/**
 * Aktiviert "Auto-Fullscreen beim ersten Tap" + Resume-Wiederherstellung
 *
 * - Registriert globalen Listener für pointerup + click (capture phase)
 * - Beim ersten Tap/Tastendruck: Fullscreen aktivieren
 * - Robuste Retry-Logik mit maxTries
 * - Nur auf Android (Pixel 4a etc.) für echtes Immersive
 * - Kein UI-Element nötig, völlig transparent für User
 * - **NEU:** Bei Resume (Rückkehr aus Multitasking) werden Tap-Listener
 *   erneut registriert, wenn Fullscreen-Präferenz gesetzt ist.
 *   → Nächster Tap stellt Fullscreen automatisch wieder her.
 *   → Respektiert User-Gesture-Pflicht für PWAs.
 *
 * Aufruf: Einmal beim App-Start (z.B. in main.tsx)
 */
export function enableAutoFullscreenOnFirstTap(): void {
  // Nur in installierten PWAs sinnvoll (nicht im Browser)
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS legacy
    (window.navigator as unknown as { standalone?: boolean }).standalone ===
      true;
  const isAndroid = /Android/i.test(navigator.userAgent);

  if (!isStandalone || !isAndroid) return;

  let tries = 0;
  const maxTries = 3;
  let tapListenersActive = false;

  const go = () => {
    // nächster Tick stabilisiert Router/Navi
    setTimeout(() => {
      requestFullscreenWithFallbacks(document.documentElement)
        .then(() => {
          localStorage.setItem(FS_PREF_KEY, "1");
          cleanTapListeners();
        })
        .catch(() => {
          if (++tries >= maxTries) cleanTapListeners();
        });
    }, 0);
  };

  function attachTapListeners() {
    if (tapListenersActive) return; // Keine doppelten Listener
    tapListenersActive = true;
    tries = 0; // Retry-Counter bei jedem Re-Arm zurücksetzen
    window.addEventListener("pointerup", go, true);
    window.addEventListener("click", go, true);
  }

  function cleanTapListeners() {
    if (!tapListenersActive) return;
    tapListenersActive = false;
    window.removeEventListener("pointerup", go, true);
    window.removeEventListener("click", go, true);
  }

  // Resume-Erkennung: Wenn die App nach Multitasking zurückkommt,
  // hat das OS den Fullscreen-Modus beendet. Wir registrieren die
  // Tap-Listener erneut, damit der nächste User-Tap Fullscreen
  // automatisch wiederherstellt (User-Gesture-Pflicht respektiert).
  const handleVisibilityChange = () => {
    if (
      document.visibilityState === "visible" &&
      !document.fullscreenElement &&
      hasFullscreenPreference()
    ) {
      console.log(
        "[Fullscreen] resume detected – re-arming fullscreen on next tap"
      );
      attachTapListeners();
    }
  };

  // Persistent Listener: Bleibt dauerhaft aktiv (überlebt Fullscreen-Zyklen)
  document.addEventListener("visibilitychange", handleVisibilityChange);

  // Initialer Attach für den ersten Tap
  attachTapListeners();
}

/**
 * Stellt Fullscreen nach File-Picker-Aktionen wieder her
 *
 * Problem: Native File-Picker (z.B. bei Video-Upload) beendet den Fullscreen-Modus.
 * Lösung: Nach erfolgreicher Dateiauswahl versuchen, Fullscreen automatisch
 * wiederherzustellen. Falls das wegen User-Gesture-Pflicht fehlschlägt,
 * werden Tap-Listener re-armed, damit der nächste Tap Fullscreen wiederherstellt.
 *
 * @param delayMs - Verzögerung in Millisekunden (default: 300ms, damit File-Picker-Dialog geschlossen ist)
 */
export function restoreFullscreenAfterFilePicker(delayMs = 300): void {
  // Nur wenn Fullscreen-Präferenz gesetzt ist
  if (!hasFullscreenPreference()) return;

  // Nur in installierten PWAs sinnvoll
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone ===
      true;
  if (!isStandalone) return;

  // Bereits im Fullscreen? Nichts tun
  if (document.fullscreenElement) return;

  // Nach kurzem Delay versuchen, Fullscreen wiederherzustellen
  setTimeout(() => {
    // Prüfe nochmal, ob wir noch nicht im Fullscreen sind
    if (document.fullscreenElement) return;

    // Versuche direkt, Fullscreen wiederherzustellen
    // Das kann fehlschlagen, wenn die User-Gesture-Pflicht noch nicht erfüllt ist
    requestFullscreenWithFallbacks(document.documentElement)
      .then(() => {
        console.log("[Fullscreen] restored after file picker");
      })
      .catch(() => {
        // Fehlgeschlagen (z.B. wegen User-Gesture-Pflicht)
        // → Re-arm Tap-Listener, damit nächster Tap Fullscreen wiederherstellt
        console.log(
          "[Fullscreen] file picker closed – re-arming fullscreen on next tap"
        );
        // Nutze die gleiche Logik wie bei Resume
        // Wir können nicht direkt auf die internen Funktionen zugreifen,
        // aber wir können einen einmaligen Tap-Listener registrieren
        let listenerAttached = false;
        const restoreOnNextTap = () => {
          if (listenerAttached) return;
          listenerAttached = true;

          const go = () => {
            setTimeout(() => {
              requestFullscreenWithFallbacks(document.documentElement)
                .then(() => {
                  console.log("[Fullscreen] restored after file picker (tap)");
                  // Cleanup
                  window.removeEventListener("pointerup", go, true);
                  window.removeEventListener("click", go, true);
                })
                .catch(() => {
                  // Ignore errors
                });
            }, 0);
          };

          window.addEventListener("pointerup", go, true);
          window.addEventListener("click", go, true);

          // Cleanup nach 5 Sekunden (falls kein Tap kommt)
          setTimeout(() => {
            window.removeEventListener("pointerup", go, true);
            window.removeEventListener("click", go, true);
          }, 5000);
        };

        // Warte auf nächsten User-Interaktion (Tap/Click)
        restoreOnNextTap();
      });
  }, delayMs);
}

/**
 * Optional: Listener für Fullscreen-Änderungen
 * Nützlich für UI-Updates (z.B. Icon wechseln)
 */
export function onFullscreenChange(cb: (isFs: boolean) => void): () => void {
  const fn = () => cb(!!document.fullscreenElement);
  document.addEventListener("fullscreenchange", fn);
  // Return cleanup function
  return () => document.removeEventListener("fullscreenchange", fn);
}
